import { datadogRum } from "@datadog/browser-rum";
import { performanceMarkers } from "@constants/datadogEvents";

/**
 * Utility functions for performance measurements
 */

/**
 * Mark a performance point in time
 * @param markerName - Name of the performance marker
 */
export const markPerformance = (markerName: string): void => {
  try {
    performance.mark(markerName);
  } catch (err) {
    console.warn(`Failed to mark performance: ${markerName}`, err);
  }
};

/**
 * Check if a performance marker exists
 * @param markerName - Name of the performance marker to check
 * @returns boolean indicating if the marker exists
 */
export const markerExists = (markerName: string): boolean => {
  try {
    const entries = performance.getEntriesByName(markerName);
    return entries.length > 0;
  } catch (err) {
    console.warn(`Failed to check if marker exists: ${markerName}`, err);
    return false;
  }
};

/**
 * Measure time between two performance markers and report to Datadog
 * @param measureName - Name of the performance measure
 * @param startMarker - Name of the start marker
 * @param endMarker - Name of the end marker
 * @param additionalData - Additional data to include in the Datadog action
 */
export const measureAndReportPerformance = (
  measureName: string,
  startMarker: string,
  endMarker: string,
  additionalData?: Record<string, unknown>,
): void => {
  try {
    // Check if both markers exist before attempting to measure
    if (!markerExists(startMarker) || !markerExists(endMarker)) {
      return;
    }

    const measure = performance.measure(measureName, startMarker, endMarker);
    // Report to Datadog
    datadogRum.addAction(measureName, {
      ms: measure.duration,
      ...additionalData,
    });

    // Clean up marks
    performance.clearMarks(startMarker);
    performance.clearMarks(endMarker);
  } catch (err) {
    console.warn(`Failed to measure performance: ${measureName}`, err);
  }
};

/**
 * Centralized object containing all performance marker functions
 */
export const marks = {
  /**
   * Mark the start of email lookup process
   */
  emailLookupStarted: (): void => {
    markPerformance("email_lookup_requested");
  },

  /**
   * Mark when OTP is requested
   */
  otpRequested: (): void => {
    markPerformance("otp_requested");
  },

  /**
   * Mark when OTP is completed
   */
  otpCompleted: (): void => {
    markPerformance(performanceMarkers.OTP_COMPLETED);
  },

  /**
   * Mark when lookup API call starts
   */
  lookupApiStarted: (): void => {
    markPerformance("lookup_api_started");
  },

  /**
   * Mark when lookup API call completes
   */
  lookupApiCompleted: (): void => {
    markPerformance("lookup_api_completed");
  },

  /**
   * Mark when a flow starts
   */
  flowStarted: (): void => {
    markPerformance("flow_started");
  },

  /**
   * Mark when a flow becomes interactive
   */
  flowInteractive: (): void => {
    markPerformance("flow_interactive");
  },
};

/**
 * Centralized object containing all performance measurement functions
 */
export const measures = {
  /**
   * Measure and report the time from email lookup to OTP request
   * @param flowType - The type of flow
   * @param additionalData - Additional data to include in the measurement
   */
  emailLookupToOtp: (flowType: string = "", additionalData: Record<string, unknown> = {}): void => {
    measureAndReportPerformance("email_lookup_to_otp", "email_lookup_requested", "otp_requested", {
      flowType,
      ...additionalData,
    });
  },

  /**
   * Measure and report the time taken by the lookup API call
   * @param flowType - The type of flow
   * @param additionalData - Additional data to include in the measurement
   */
  lookupApiDuration: (flowType: string = "", additionalData: Record<string, unknown> = {}): void => {
    measureAndReportPerformance("lookup_api_duration", "lookup_api_started", "lookup_api_completed", {
      flowType,
      ...additionalData,
    });
  },

  /**
   * Measure and report the time from flow start to interactive
   * @param flowType - The type of flow
   * @param additionalData - Additional data to include in the measurement
   */
  flowStartToInteractive: (flowType: string = "", additionalData: Record<string, unknown> = {}): void => {
    measureAndReportPerformance("flow_start_to_interactive", "flow_started", "flow_interactive", {
      flowType,
      ...additionalData,
    });
  },
};
